import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import socketService from '../services/socketService';
import { diagnosticarUsuario, podeTransferir } from '../utils/authUtils';

const useAtendimentoHandlers = ({
  senhaChamada,
  atendimentoIniciado,
  atendimentoFinalizado,
  tempoAtendimento,
  senhasProcessadas,
  setSenhaChamada,
  setAtendimentoIniciado,
  setAtendimentoFinalizado,
  setTempoAtendimento,
  setSenhasProcessadas,
  setDialogoAberto,
  setDialogoReabrirSenhaAberto,
  setDialogoChamarSenhaEspecifica,
  setDialogoAlterarServicoAberto,
  setDialogoConfirmarAlteracaoAberto,
  setAcaoEscolhida,
  setAtendenteEscolhido,
  acaoEscolhida,
  atendenteEscolhido,
  setSenhaSelecionada,
  setRechamando,
  setProximasSenhas,
  proximasSenhas,
  limparEstadoAtendimento,
  salvarEstadoAtendimento,
  buscarSenhas,
  buscarEstatisticas,
  timerRef,
  user,
  guicheSelecionado,
  rechamarSenhaViaSocket
}) => {

  // Manipulador de início de atendimento
  const handleIniciarAtendimento = useCallback(async () => {
    if (senhaChamada && !atendimentoIniciado) {
      try {
        const dataInicio = new Date();
        
        const response = await api.post(`/senhas/${senhaChamada.id}/iniciar`, {
            dataHoraIniciado: dataInicio.toISOString()
        });
        
        if (!response.data.success) {
          throw new Error(response.data.message || 'Erro ao iniciar atendimento no servidor');
        }
        
        setAtendimentoIniciado(true);
        setAtendimentoFinalizado(false);
        setTempoAtendimento(0);
        
        const dados = {
          senha: senhaChamada,
          iniciado: true,
          finalizado: false,
          iniciadoEm: dataInicio.toISOString(),
          tempoDecorrido: 0,
          timestamp: dataInicio.toISOString()
        };
        
        if (user && user.id) {
          localStorage.setItem(`atendimento_${user.id}`, JSON.stringify(dados));
        }
        
        toast.success('Atendimento iniciado!');
      } catch (error) {
        console.error('Erro ao iniciar atendimento:', error);
        toast.error('Erro ao iniciar atendimento. Por favor, tente novamente.');
      }
    }
  }, [senhaChamada, atendimentoIniciado, setAtendimentoIniciado, setAtendimentoFinalizado, setTempoAtendimento, user]);
  
  // Manipulador de finalização de atendimento
  const handleFinalizarAtendimento = useCallback(async () => {
    if (senhaChamada && senhaChamada.id && atendimentoIniciado) {
      try {
        const response = await api.post(`/senhas/${senhaChamada.id}/finalizar`, {
            tempoAtendimento: tempoAtendimento,
            dataHoraFinalizado: new Date().toISOString()
        });
        
        if (response.data.success) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          
          setSenhasProcessadas([...senhasProcessadas, {
            ...senhaChamada,
            tempoAtendimento,
            resultado: 'Finalizado'
          }]);
          
          setAtendimentoFinalizado(true);
          setAtendimentoIniciado(false);
          
          limparEstadoAtendimento();
          
          // Atualizar estatísticas imediatamente após finalização
          if (buscarEstatisticas) {
            buscarEstatisticas();
          }
          
          toast.success('Atendimento finalizado com sucesso!');
        } else {
          console.error('Erro ao finalizar atendimento:', response.data.error);
          toast.error('Erro ao finalizar atendimento. Por favor, tente novamente.');
        }
      } catch (error) {
        console.error('Erro ao comunicar com o servidor:', error);
        toast.error('Erro ao comunicar com o servidor. Por favor, tente novamente.');
      }
    }
  }, [senhaChamada, atendimentoIniciado, tempoAtendimento, timerRef, senhasProcessadas, setSenhasProcessadas, setAtendimentoFinalizado, setAtendimentoIniciado, limparEstadoAtendimento, buscarEstatisticas]);

  // Manipulador para mostrar o diálogo de não compareceu
  const handleMostrarDialogoNaoCompareceu = useCallback(() => {
    setDialogoAberto(true);
  }, [setDialogoAberto]);

  // Manipulador para confirmar não compareceu
  const handleConfirmarNaoCompareceu = useCallback(async () => {
    if (senhaChamada && senhaChamada.id) {
      try {
        const response = await api.post(`/senhas/${senhaChamada.id}/cancelar`);

        if (response.data.success) {
          setSenhasProcessadas([...senhasProcessadas, {
            ...senhaChamada,
            tempoAtendimento: 0,
            resultado: 'Não Compareceu'
          }]);
          
          setSenhaChamada(null);
          setAtendimentoIniciado(false);
          setAtendimentoFinalizado(false);
          setTempoAtendimento(0);
          
          limparEstadoAtendimento();
          
          // Atualizar estatísticas imediatamente após cancelamento
          if (buscarEstatisticas) {
            buscarEstatisticas();
          }
          
          toast.success('Senha cancelada por não comparecimento');
        } else {
          console.error('Erro ao cancelar senha:', response.data.error);
          toast.error('Erro ao cancelar senha. Por favor, tente novamente.');
        }
      } catch (error) {
        console.error('Erro ao comunicar com o servidor:', error);
        toast.error('Erro ao comunicar com o servidor. Por favor, tente novamente.');
      }
    }
    
    setDialogoAberto(false);
  }, [senhaChamada, senhasProcessadas, setSenhasProcessadas, setSenhaChamada, setAtendimentoIniciado, setAtendimentoFinalizado, setTempoAtendimento, limparEstadoAtendimento, setDialogoAberto, buscarEstatisticas]);
  
  // Manipulador para cancelar não compareceu
  const handleCancelarNaoCompareceu = useCallback(() => {
    setDialogoAberto(false);
  }, [setDialogoAberto]);
  
  // Manipulador para mostrar o diálogo de reabrir senha
  const handleMostrarDialogoReabrirSenha = useCallback(() => {
    setDialogoReabrirSenhaAberto(true);
  }, [setDialogoReabrirSenhaAberto]);
  
  // Manipulador para confirmar reabrir senha (voltar para fila)
  const handleConfirmarReabrirSenha = useCallback(async () => {
    // Fechar o modal de escolha e abrir modal de confirmação de alteração
    setDialogoReabrirSenhaAberto(false);
    setAcaoEscolhida('fila');
    setAtendenteEscolhido(null);
    setDialogoConfirmarAlteracaoAberto(true);
  }, [setDialogoReabrirSenhaAberto, setAcaoEscolhida, setAtendenteEscolhido, setDialogoConfirmarAlteracaoAberto]);

  // Função para executar a ação de voltar para fila (com ou sem alteração de serviço)
  const executarVoltarParaFila = useCallback(async (novoServicoNome = null) => {
    if (senhaChamada && senhaChamada.id) {
      try {
        let response;
        
        if (novoServicoNome) {
          // Primeiro alterar o serviço, depois reabrir
          response = await api.post(`/senhas/${senhaChamada.id}/alterar-servico`, {
            servico_nome: novoServicoNome
          });
        } else {
          // Apenas reabrir sem alterar serviço
          response = await api.post(`/senhas/${senhaChamada.id}/reabrir`);
        }

        if (response.data.success) {
          setSenhaChamada(null);
          setAtendimentoIniciado(false);
          setAtendimentoFinalizado(false);
          setTempoAtendimento(0);
          
          limparEstadoAtendimento();

          await buscarSenhas();
          
          const mensagem = novoServicoNome 
            ? `Senha reaberta e serviço alterado para ${novoServicoNome} com sucesso!`
            : 'Senha reaberta com sucesso';
          toast.success(mensagem);
        } else {
          console.error('Erro ao processar senha:', response.data.error);
          toast.error('Erro ao processar senha. Por favor, tente novamente.');
        }
      } catch (error) {
        console.error('Erro ao comunicar com o servidor:', error);
        toast.error('Erro ao comunicar com o servidor. Por favor, tente novamente.');
      }
    }
  }, [senhaChamada, setSenhaChamada, setAtendimentoIniciado, setAtendimentoFinalizado, setTempoAtendimento, limparEstadoAtendimento, buscarSenhas]);
  
  // Manipulador para cancelar reabrir senha
  const handleCancelarReabrirSenha = useCallback(() => {
    setDialogoReabrirSenhaAberto(false);
  }, [setDialogoReabrirSenhaAberto]);
  
  // Manipulador para reabrir senha
  const handleReabrirSenha = useCallback(() => {
    handleMostrarDialogoReabrirSenha();
  }, [handleMostrarDialogoReabrirSenha]);

  // Manipulador para rechamar a senha atual
  const handleRechamarSenha = useCallback(() => {
    if (!senhaChamada || !senhaChamada.numero) {
      toast.error('Não há senha em atendimento para rechamar');
      return;
    }

    setRechamando(true);
    
    const sucesso = rechamarSenhaViaSocket(
      senhaChamada.numero,
      guicheSelecionado,
      senhaChamada.preferencial || false
    );

    if (!sucesso) {
      setRechamando(false);
    }
    
    setTimeout(() => {
      setRechamando(false);
    }, 2000);
  }, [senhaChamada, guicheSelecionado, setRechamando, rechamarSenhaViaSocket]);

  // Manipulador para clicar em uma senha específica
  const handleClicarSenha = useCallback((senha) => {
    if (senhaChamada && !atendimentoFinalizado) {
      alert('Finalize o atendimento atual antes de chamar uma nova senha.');
      return;
    }
    
    setSenhaSelecionada({
      ...senha,
      id: senha.id
    });
    setDialogoChamarSenhaEspecifica(true);
  }, [senhaChamada, atendimentoFinalizado, setSenhaSelecionada, setDialogoChamarSenhaEspecifica]);

  // Manipulador para cancelar a chamada da senha específica
  const handleCancelarChamarSenhaEspecifica = useCallback(() => {
    setDialogoChamarSenhaEspecifica(false);
    setSenhaSelecionada(null);
  }, [setDialogoChamarSenhaEspecifica, setSenhaSelecionada]);

  // Estado para loading da transferência
  const [transferindo, setTransferindo] = useState(false);

  // Manipulador para escolher atendente (primeiro passo)
  const handleEscolherAtendente = useCallback((senhaId, atendente) => {
    console.group('🔄 HANDLE ESCOLHER ATENDENTE');
    console.log('📋 senhaId recebido:', senhaId);
    console.log('👤 atendente recebido:', atendente);
    console.log('🆔 atendente.id:', atendente?.id);
    console.log('📛 atendente.nome:', atendente?.nome);
    console.log('🏷️ typeof atendente:', typeof atendente);
    console.groupEnd();

    // Fechar o modal de escolha e abrir modal de confirmação de alteração
    setDialogoReabrirSenhaAberto(false);
    setAcaoEscolhida('atendente');
    setAtendenteEscolhido(atendente); // Agora está recebendo o objeto correto
    setDialogoConfirmarAlteracaoAberto(true);
  }, [setDialogoReabrirSenhaAberto, setAcaoEscolhida, setAtendenteEscolhido, setDialogoConfirmarAlteracaoAberto]);

  // Manipulador para enviar senha para outro atendente (TRANSFERÊNCIA)
  const handleEnviarParaAtendente = useCallback(async (senhaId, atendente, motivo = null) => {
    // 🔍 DIAGNÓSTICO: Verificar estado do usuário
    console.group('🔄 INÍCIO DA TRANSFERÊNCIA DE SENHA');
    const diagnostico = diagnosticarUsuario(user, 'handleEnviarParaAtendente');
    console.log('📋 Parâmetros recebidos:', { senhaId, atendente, motivo });
    console.groupEnd();

    if (transferindo) {
      toast.warn('Transferência já em andamento. Aguarde...');
      return;
    }

    // ✅ VALIDAÇÃO CRÍTICA: Verificar se o usuário pode transferir
    if (!podeTransferir(user)) {
      console.error('❌ Usuário não pode realizar transferências');
      toast.error('Erro: Você não tem permissão para transferir senhas. Faça login novamente.');
      return;
    }

    // ✅ VALIDAÇÃO CRÍTICA: Verificar dados obrigatórios
    if (!senhaId) {
      console.error('❌ ID da senha não fornecido para transferência');
      toast.error('Erro: ID da senha não encontrado.');
      return;
    }

    // ✅ VALIDAÇÃO E NORMALIZAÇÃO DO ATENDENTE: Aceitar tanto objeto quanto ID
    let atendenteDestino;
    if (typeof atendente === 'number') {
      // Se recebeu apenas o ID, criar objeto básico
      atendenteDestino = {
        id: atendente,
        nome: `Atendente ${atendente}`,
        guiche: 1 // Valor padrão
      };
      console.log('🔄 Atendente normalizado de ID para objeto:', atendenteDestino);
    } else if (typeof atendente === 'object' && atendente !== null) {
      // Se recebeu um objeto, verificar se tem ID
      if (!atendente.id) {
        console.error('❌ Objeto atendente sem propriedade ID:', atendente);
        toast.error('Erro: Atendente de destino não tem ID válido.');
        return;
      }
      atendenteDestino = atendente;
    } else {
      console.error('❌ Atendente destino inválido (nem número nem objeto):', atendente);
      toast.error('Erro: Atendente de destino não identificado.');
      return;
    }

    console.log('✅ Atendente de destino validado:', atendenteDestino);

    setTransferindo(true);

    try {
      console.log('🔄 Iniciando transferência de senha:', { 
        senhaId, 
        atendenteOrigemId: user.id,
        atendenteDestinoId: atendenteDestino.id,
        guiche: atendenteDestino.guiche || 1,
        motivo 
      });
      
      // Verificar se WebSocket está disponível
      if (socketService.isConnected()) {
        console.log('✅ WebSocket conectado, usando transferência em tempo real');
        
        try {
          // Usar o método de transferência com fallback automático
          const resultado = await socketService.transferirSenhaComFallback(
            senhaId,
            user.id, // ID do atendente que está transferindo
            atendenteDestino.id, // ID do atendente destino
            atendenteDestino.guiche || 1, // Guichê do atendente destino
            motivo // Motivo da transferência
          );

          console.log('✅ Transferência bem-sucedida via WebSocket:', resultado);
          
          // Limpar o estado atual do atendimento
          setSenhaChamada(null);
          setAtendimentoIniciado(false);
          setAtendimentoFinalizado(false);
          setTempoAtendimento(0);
          limparEstadoAtendimento();
          
          // Atualizar a lista de senhas
          await buscarSenhas();

          // ✅ Notificação será mostrada apenas no fallback HTTP para evitar duplicatas
          console.log('✅ Transferência WebSocket realizada com sucesso');
          return;

        } catch (wsError) {
          console.warn('❌ Falha na transferência via WebSocket:', wsError.message);
          toast.warn('Falha na transferência em tempo real. Tentando método alternativo...');
        }
      }

      // Fallback HTTP se WebSocket falhar ou não estiver disponível
      console.log('🔄 Usando fallback HTTP para transferência');
      
      // Verificação adicional: buscar status atual do atendente antes de enviar
      const responseStatusAtendente = await api.get(`/senhas/atendente/${atendenteDestino.id}/em-atendimento`);
      
      if (responseStatusAtendente.data.success && responseStatusAtendente.data.data) {
        // Atendente tem senha em atendimento
        const senhaAtual = responseStatusAtendente.data.data;
        toast.error(`O atendente ${atendenteDestino.nome} já está ocupado atendendo a senha ${senhaAtual.numero}. Selecione outro atendente.`);
        return;
      }
      
      // Usar o endpoint HTTP de transferência
      const response = await api.post(`/senhas/${senhaId}/transferir`, { 
        senhaId,
        atendenteOrigemId: user.id,
        atendenteDestinoId: atendenteDestino.id,
        guiche: atendenteDestino.guiche || 1,
        motivo
      });
      
      const data = await response.data;
      console.log('✅ Resposta da transferência HTTP:', data);
      
      if (data && data.success) {
        toast.success(`Senha transferida para ${atendenteDestino.nome} com sucesso!`);
        
        // Limpar o estado atual do atendimento
        setSenhaChamada(null);
        setAtendimentoIniciado(false);
        setAtendimentoFinalizado(false);
        setTempoAtendimento(0);
        limparEstadoAtendimento();
        
        // Atualizar a lista de senhas
        await buscarSenhas();
      } else {
        // Melhor tratamento do erro baseado na resposta
        const errorMessage = data?.error || data?.message || 'Erro desconhecido ao transferir senha';
        console.error('❌ Erro detalhado na transferência:', data);
        throw new Error(errorMessage);
      }

    } catch (error) {
      console.error('❌ Erro na transferência:', error);
      
      // Se o erro for 404 ou similar, significa que o atendente não tem senha em atendimento (está disponível)
      if (error.response?.status === 404 || error.response?.data?.message?.includes('Nenhuma senha em atendimento')) {
        try {
          // Prosseguir com o envio da senha usando método legado
          const response = await api.post(`/senhas/${senhaId}/chamar`, { 
            atendenteId: atendenteDestino.id,
            guiche: atendenteDestino.guiche || 1,
            iniciarAtendimento: false
          });
          
          const data = await response.data;
          console.log('✅ Resposta do envio legado para atendente:', data);
          
          if (data.success) {
            toast.success(`Senha enviada para ${atendenteDestino.nome}! O atendente precisará iniciar o atendimento manualmente.`);
            
            // Limpar o estado atual do atendimento
            setSenhaChamada(null);
            setAtendimentoIniciado(false);
            setAtendimentoFinalizado(false);
            setTempoAtendimento(0);
            limparEstadoAtendimento();
            
            // Atualizar a lista de senhas
            await buscarSenhas();
          } else {
            toast.error(data.message || 'Erro ao enviar senha para o atendente');
          }
        } catch (secondError) {
          toast.error('Erro ao conectar ao servidor. Verifique sua conexão.');
          console.error('Erro ao enviar senha para atendente (segunda tentativa):', secondError);
        }
      } else {
        // Mostrar mensagem de erro mais específica baseada no tipo de erro
        if (error.message && error.message.includes('obrigatório')) {
          toast.error(`Erro de validação: ${error.message}`);
        } else {
          toast.error(error.message || 'Erro ao transferir senha. Tente novamente.');
        }
        console.error('Erro na transferência:', error);
      }
    } finally {
      setTransferindo(false);
    }
  }, [transferindo, user, setSenhaChamada, setAtendimentoIniciado, setAtendimentoFinalizado, setTempoAtendimento, limparEstadoAtendimento, buscarSenhas]);

  // Manipulador para mostrar o diálogo de alterar serviço
  const handleMostrarDialogoAlterarServico = useCallback(() => {
    setDialogoAlterarServicoAberto(true);
  }, [setDialogoAlterarServicoAberto]);

  // Manipulador para cancelar alterar serviço
  const handleCancelarAlterarServico = useCallback(() => {
    setDialogoAlterarServicoAberto(false);
  }, [setDialogoAlterarServicoAberto]);

  // Manipulador para quando o usuário escolhe NÃO alterar o serviço
  const handleNaoAlterarServico = useCallback(async () => {
    setDialogoConfirmarAlteracaoAberto(false);
    
    if (acaoEscolhida === 'fila') {
      // Executar ação de voltar para fila sem alterar serviço
      await executarVoltarParaFila();
    } else if (acaoEscolhida === 'atendente' && atendenteEscolhido) {
      // Executar ação de enviar para atendente sem alterar serviço
      await handleEnviarParaAtendente(senhaChamada?.id, atendenteEscolhido);
    }
    
    // Limpar estados
    setAcaoEscolhida(null);
    setAtendenteEscolhido(null);
  }, [setDialogoConfirmarAlteracaoAberto, acaoEscolhida, atendenteEscolhido, executarVoltarParaFila, handleEnviarParaAtendente, senhaChamada, setAcaoEscolhida, setAtendenteEscolhido]);

  // Manipulador para quando o usuário escolhe SIM alterar o serviço
  const handleSimAlterarServico = useCallback(() => {
    setDialogoConfirmarAlteracaoAberto(false);
    setDialogoAlterarServicoAberto(true);
  }, [setDialogoConfirmarAlteracaoAberto, setDialogoAlterarServicoAberto]);

  // Manipulador para confirmar alterar serviço (executado após escolher o novo serviço)
  const handleConfirmarAlteracaoComNovoServico = useCallback(async (novoServicoNome) => {
    try {
      setDialogoAlterarServicoAberto(false);
      
      if (acaoEscolhida === 'fila') {
        // Executar ação de voltar para fila COM alteração de serviço
        await executarVoltarParaFila(novoServicoNome);
      } else if (acaoEscolhida === 'atendente' && atendenteEscolhido) {
        // Primeiro alterar o serviço, depois enviar para atendente
        if (senhaChamada?.id) {
          const alterarResponse = await api.post(`/senhas/${senhaChamada.id}/alterar-servico`, {
            servico_nome: novoServicoNome
          });
          
          if (alterarResponse.data.success) {
            // Agora enviar para o atendente
            await handleEnviarParaAtendente(senhaChamada.id, atendenteEscolhido);
          } else {
            toast.error('Erro ao alterar serviço da senha');
          }
        }
      }
      
      // Limpar estados
      setAcaoEscolhida(null);
      setAtendenteEscolhido(null);
      
      if (buscarEstatisticas) {
        buscarEstatisticas();
      }
    } catch (error) {
      console.error('Erro ao processar alteração de serviço:', error);
      toast.error('Erro ao processar alteração de serviço');
    }
  }, [acaoEscolhida, atendenteEscolhido, senhaChamada, executarVoltarParaFila, handleEnviarParaAtendente, setDialogoAlterarServicoAberto, setAcaoEscolhida, setAtendenteEscolhido, buscarEstatisticas]);

  // Manipulador para cancelar modal de confirmação
  const handleCancelarConfirmacaoAlteracao = useCallback(() => {
    setDialogoConfirmarAlteracaoAberto(false);
    setAcaoEscolhida(null);
    setAtendenteEscolhido(null);
  }, [setDialogoConfirmarAlteracaoAberto, setAcaoEscolhida, setAtendenteEscolhido]);

  return {
    handleIniciarAtendimento,
    handleFinalizarAtendimento,
    handleMostrarDialogoNaoCompareceu,
    handleConfirmarNaoCompareceu,
    handleCancelarNaoCompareceu,
    handleMostrarDialogoReabrirSenha,
    handleConfirmarReabrirSenha,
    handleCancelarReabrirSenha,
    handleReabrirSenha,
    handleRechamarSenha,
    handleClicarSenha,
    handleCancelarChamarSenhaEspecifica,
    handleEnviarParaAtendente,
    handleEscolherAtendente,
    handleMostrarDialogoAlterarServico,
    handleCancelarAlterarServico,
    handleNaoAlterarServico,
    handleSimAlterarServico,
    handleConfirmarAlteracaoComNovoServico,
    handleCancelarConfirmacaoAlteracao,
    executarVoltarParaFila,
    transferindo
  };
};

export default useAtendimentoHandlers; 